/* eslint-disable no-unused-vars */
import { Injector } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { isEmpty, map, mapTo, tap } from 'rxjs/operators';
import { CreateAccount } from 'src/app/accounts/interfaces/accounts.types';
import { AccountsService } from 'src/app/accounts/services/accounts.service';
import { MeService } from 'src/app/me/services/me.service';
import {
  OnboardingIntentStep,
  OnboardingType,
} from 'src/app/shared/enums/intent.enum';
import {
  IntentProperties,
  UserIntentState,
} from 'src/app/shared/interfaces/intent.interface';
import { environment } from 'src/environments/environment';
import { IntentService } from '../intent.service';
import { BLOOD_STEPS, DNA_STEPS } from '../steps';
import { IntentStrategy, IntentStrategyConfig } from './intent.strategy';
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as Sentry from '@sentry/angular';

export class KitIntentStrategy implements IntentStrategy {
  private intentService: IntentService;
  private meService: MeService;
  private router: Router;
  private accountService: AccountsService;

  constructor(
    private injector: Injector,
    private onboardingType: OnboardingType
  ) {
    this.intentService = this.injector.get(IntentService);
    this.meService = this.injector.get(MeService);
    this.router = this.injector.get(Router);
    this.accountService = this.injector.get(AccountsService);
  }

  public getConfig(): IntentStrategyConfig {
    const PREFIX = 'ONBOARDING.KIT.';
    const type = this.onboardingType;

    return {
      steps: type === OnboardingType.DNA ? DNA_STEPS : BLOOD_STEPS,
      type,
      [OnboardingIntentStep.Welcome]: {
        title: PREFIX + 'WELCOME.WELCOME',
        description: PREFIX + 'WELCOME.YOU_ARE_ABOUT_TO_BEGIN',
      },
      [OnboardingIntentStep.Caution]: {
        paragraph1: 'ACCOUNTS.CAUTION.GENERAL.PARAGRAPH_1',
        paragraph2: 'ACCOUNTS.CAUTION.GENERAL.PARAGRAPH_2',
      },
      [OnboardingIntentStep.Complete]: {
        title: PREFIX + 'COMPLETED.COMPLETE',
        description:
          type === OnboardingType.BLOOD
            ? PREFIX + 'COMPLETED.ONCE_THE_ANALYSIS'
            : PREFIX + 'COMPLETED.WE_WILL_SEND_YOU',
      },
    };
  }

  public onAppInit(milestone: OnboardingIntentStep): void {
    this.redirectToMilestone(milestone);
  }

  public onWelcomeInit(forceInit: boolean): Observable<any> {
    const { blood, dna } = environment.intents;
    const intentId = this.onboardingType === OnboardingType.DNA ? dna : blood;

    if (
      !this.intentService.isUserInIntent(this.intentService.state$.value) ||
      forceInit
    ) {
      return this.intentService.startIntent(intentId);
    } else {
      return of({});
    }
  }

  public onWelcomeLogin(): void {
    this.onNextStep(OnboardingIntentStep.Login).subscribe();
  }

  public onWelcomeCreateAccount(): void {
    this.onNextStep(OnboardingIntentStep.Register).subscribe();
  }

  public onRegisterCreateAccount({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    skipEmailVerification,
    ...rest
  }: CreateAccount): Observable<any> {
    return this.accountService.create(rest).pipe(
      tap(() => {
        this.onNextStep(OnboardingIntentStep.Newsletter).subscribe();
      })
    );
  }

  public onRegisterLogin(): void {
    this.onNextStep(OnboardingIntentStep.Login).subscribe();
  }

  public onNewsletterNextStep(): void {
    this.onNextStep(OnboardingIntentStep.Caution).subscribe();
  }

  public onCautionNextStep(): void {
    this.onNextStep(OnboardingIntentStep.Check).subscribe();
  }

  public onNotValidatedEmail(): Observable<boolean> {
    return this.onNextStep(OnboardingIntentStep.Check).pipe(mapTo(false));
  }

  public onVerifyGoToLogin(): void {
    this.onNextStep(OnboardingIntentStep.Login).subscribe();
  }

  public onEmailActivation(): Observable<boolean> {
    if (
      this.intentService.state$?.value?.milestoneState !==
      OnboardingIntentStep.Login
    ) {
      return this.onNextStep(OnboardingIntentStep.Login).pipe(mapTo(false));
    } else {
      return of(true);
    }
  }

  public onSkipEmailActivation(): Observable<boolean> {
    return this.onNextStep(OnboardingIntentStep.Onboarding).pipe(mapTo(false));
  }

  public onDownloadAfterCheckSkipClick(): void {
    this.onNextStep(OnboardingIntentStep.Onboarding).subscribe();
  }

  public onAlreadyActivitedEmailGoToLogin(): void {
    this.onNextStep(OnboardingIntentStep.Login).subscribe();
  }

  public onLoginRegister(): void {
    this.onNextStep(OnboardingIntentStep.Register).subscribe();
  }

  public onLoginSuccessfully(): void {
    const currentStep = this.intentService.state$.value.milestoneState;

    const stepsToSkip = [
      OnboardingIntentStep.Newsletter,
      OnboardingIntentStep.Caution,
      OnboardingIntentStep.Check,
      OnboardingIntentStep.Login,
      OnboardingIntentStep.Register,
    ];

    if (stepsToSkip.includes(currentStep)) {
      this.onNextStep(OnboardingIntentStep.DownloadAfterCheck).subscribe();
    } else {
      this.redirectToMilestone(currentStep);
    }
  }

  public onCreateCustomerSuccessfully(): void {
    this.onNextStep(OnboardingIntentStep.DownloadAfterOnBoarding).subscribe();
  }

  public onTrackSkipClick(): void {
    this.onNextStep(OnboardingIntentStep.Claim).subscribe();
  }

  public onKitClaimClaimClick(
    isEU: string,
    collector: string
  ): Observable<any> {
    return this.onNextStep(OnboardingIntentStep.Sample, { isEU, collector });
  }

  public onKitConsentAgree() {
    this.meService.saveConsent(true).subscribe();
    this.onNextStep(OnboardingIntentStep.Sample).subscribe();
  }

  public onKitConsentDisagree() {
    this.onNextStep(OnboardingIntentStep.Sample).subscribe();
  }

  public onKitSampleSampleTaken() {
    this.onNextStep(OnboardingIntentStep.Complete)
      .pipe(isEmpty())
      .subscribe((empty: boolean) => {
        if (empty) {
          Sentry.captureException(
            `[INTENT error] Using fallback for missing intent on onKitSampleSampleTaken`
          );
          this.router.navigate(['/dashboard']);
        }
      });
  }

  public onCompleteCloseClick(): void {
    this.onNextStep(OnboardingIntentStep.End)
      .pipe(this.handleFallback('onCompleteCloseClick'))
      .subscribe();
  }

  public onCustomerNotExists(): Observable<boolean> {
    return this.onNextStep(OnboardingIntentStep.Onboarding).pipe(mapTo(false));
  }

  public onCustomerExists(): Observable<boolean> {
    return this.onNextStep(OnboardingIntentStep.Claim).pipe(mapTo(false));
  }

  public onCustomerWelcomeExists(): Observable<boolean> {
    return this.onNextStep(OnboardingIntentStep.Welcome).pipe(map(() => false));
  }

  public onUserNotExists(): Observable<boolean> {
    return this.onNextStep(OnboardingIntentStep.Login).pipe(mapTo(false));
  }

  private onNextStep(
    milestone?: OnboardingIntentStep,
    properties?: IntentProperties
  ): Observable<UserIntentState | unknown> {
    return this.intentService
      .nextStep(milestone, properties)
      .pipe(
        tap(({ milestoneState }) => this.redirectToMilestone(milestoneState))
      );
  }

  private redirectToMilestone(milestone: OnboardingIntentStep) {
    const { steps } = this.getConfig();
    const { path, state, queryParams } = steps[milestone];

    if (this.router.url === path) {
      return;
    }

    this.router.navigate([path], {
      state,
      queryParams,
      queryParamsHandling: 'preserve',
    });
  }

  private handleFallback<T>(
    name: string
  ): (source$: Observable<T>) => Observable<boolean> {
    return (source$) =>
      source$.pipe(
        isEmpty(),
        tap((empty: boolean) => {
          if (empty) {
            Sentry.captureException(
              `[INTENT error] Using fallback for missing intent on ${name}`
            );
            this.router.navigate(['/dashboard']);
          }
        })
      );
  }
}
